// Admin Abuse Flags panel — shows unresolved abuse flags
import { useState, useEffect } from "react";
import { api } from "@/services/api";
import { Card, LoadingSpinner, Button, Alert } from "@/components/shared";
import { formatDateTime } from "@/utils/format";
import { AlertTriangle, CheckCircle } from "lucide-react";

interface AbuseFlag {
  id: string;
  agent_id: string;
  agent_name: string;
  reason: string;
  details: Record<string, unknown> | null;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution: string | null;
}

export function AbuseFlagList() {
  const [flags, setFlags] = useState<AbuseFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);

  useEffect(() => {
    loadFlags();
  }, []);

  async function loadFlags() {
    try {
      const data = await api.get<{ data: AbuseFlag[] }>("/management/abuse-flags");
      setFlags(data.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function resolveFlag(flagId: string, resolution: string) {
    setResolving(flagId);
    try {
      await api.post(`/management/abuse-flags/${flagId}/resolve`, { resolution });
      await loadFlags();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setResolving(null);
    }
  }

  if (loading) return <LoadingSpinner />;

  const REASON_LABELS: Record<string, string> = {
    rate_limit: "Rate Limit Exceeded",
    owner_report: "Owner Report",
    spam: "Spam",
  };

  return (
    <div>
      {error && <Alert type="error" className="mb-4">{error}</Alert>}
      {flags.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <CheckCircle className="h-8 w-8 text-mint mx-auto mb-2" />
            <p className="text-white/50">No unresolved abuse flags</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {flags.map((flag) => (
            <Card key={flag.id}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-white font-medium">{flag.agent_name}</p>
                    <p className="text-sm text-amber-400">{REASON_LABELS[flag.reason] || flag.reason}</p>
                    {flag.details && (
                      <p className="text-xs text-white/40 mt-1">
                        {flag.reason === "rate_limit" && `${(flag.details as any).count} messages in window (limit: ${(flag.details as any).threshold})`}
                      </p>
                    )}
                    <p className="text-xs text-white/30 mt-1">Flagged {formatDateTime(flag.created_at)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {flag.resolved_at ? (
                    <span className="text-xs text-mint">Resolved</span>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={resolving === flag.id}
                        onClick={() => resolveFlag(flag.id, "Reviewed and dismissed")}
                      >
                        Dismiss
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={resolving === flag.id}
                        onClick={() => resolveFlag(flag.id, "Warning issued to agent")}
                      >
                        Warn Agent
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
